import React, { useEffect, useState, useCallback } from "react";
import "./Holiday.css";
import {
  Calendar,
  MapPin,
  Clock,
  Search,
  CalendarDays,
  Sparkles,
  PartyPopper,
  Snowflake,
  Sun,
  Umbrella,
  Leaf,
} from "lucide-react";
import { getCompanyHolidays } from "../../service/attendance_service";

const Holiday = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCompanyHolidays();
      if (response.status === "success") {
        setHolidays(response.data);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const getDayName = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", { weekday: "long" });
  };

  const getMonthName = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "long" });
  };

  const getDayNumber = (dateStr) => {
    return new Date(dateStr).getDate();
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filteredHolidays = holidays.filter(
    (h) =>
      h.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getMonthName(h.date).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getHolidayIcon = (month) => {
    const monthLower = month.toLowerCase();
    if (monthLower.includes("january") || monthLower.includes("december"))
      return <Snowflake className="icon-pulse" size={24} color="#0ea5e9" />;
    if (monthLower.includes("april") || monthLower.includes("may"))
      return <Sun className="icon-spin" size={24} color="#f59e0b" />;
    if (monthLower.includes("october") || monthLower.includes("november"))
      return <Leaf className="icon-float" size={24} color="#10b981" />;
    if (
      monthLower.includes("june") ||
      monthLower.includes("july") ||
      monthLower.includes("august")
    )
      return <Umbrella className="icon-bounce" size={24} color="#8b5cf6" />;
    return <PartyPopper className="icon-pulse" size={24} color="#f43f5e" />;
  };

  return (
    <div className="holiday-container">
      <div className="holiday-header">
        <div className="header-content">
          <div className="header-badge">
            <Sparkles size={12} />
            <span>Company Calendar 2026</span>
          </div>
          <h1>Public Holidays</h1>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search holiday..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="holiday-summary">
        <div className="summary-card total">
          <div className="summary-icon">
            <Calendar size={20} />
          </div>
          <div className="summary-info">
            <span className="label">Total Holidays</span>
            <span className="value">{holidays.length}</span>
          </div>
        </div>
        <div className="summary-card remaining">
          <div className="summary-icon">
            <Clock size={20} />
          </div>
          <div className="summary-info">
            <span className="label">Upcoming</span>
            <span className="value">
              {holidays.filter((h) => new Date(h.date) >= today).length}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loader"></div>
          <p>Fetching holiday schedule...</p>
        </div>
      ) : (
        <div className="holiday-grid">
          {filteredHolidays.length > 0 ? (
            filteredHolidays.map((holiday, index) => {
              const isPast = new Date(holiday.date) < today;
              return (
                <div
                  key={index}
                  className={`holiday-card-premium ${isPast ? "past-holiday" : ""}`}
                >
                  <div className="holiday-card-bg"></div>
                  <div className="card-top">
                    <div className="date-badge">
                      <span className="month">
                        {getMonthName(holiday.date).substring(0, 3)}
                      </span>
                      <span className="day">{getDayNumber(holiday.date)}</span>
                    </div>
                    <div className="holiday-visual">
                      {getHolidayIcon(getMonthName(holiday.date))}
                    </div>
                  </div>

                  <div className="card-body">
                    <h3 className="holiday-title">{holiday.title}</h3>
                    <div className="holiday-meta">
                      <div className="meta-item">
                        <CalendarDays size={14} />
                        <span>{getDayName(holiday.date)}</span>
                      </div>
                      <div className="meta-item">
                        <MapPin size={14} />
                        <span>Office Closed</span>
                      </div>
                    </div>
                  </div>

                  <div className="card-footer">
                    <div
                      className={`status-pill ${isPast ? "past" : "upcoming"}`}
                    >
                      {isPast ? "Past Holiday" : "Official Holiday"}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-results">
              <div className="no-results-icon">
                <Search size={48} />
              </div>
              <h3>No holidays found</h3>
              <p>Try adjusting your search or filters.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Holiday;
